import { Injectable } from '@angular/core';
import jsPDF, { TextOptionsLight } from 'jspdf';
import { PathService } from './path.service';
import { LangDataService } from './lang-data.service';
import { IProduct } from './IProduct';
import { IHint } from './IHint';
import { IModel } from './IModel';
import { SpecialColorSelection } from '../shared/SpecialColorSelection';
import { IMetalColor } from './IMetalColor';
import { IContactData } from "./IContactData";
import { FormatterService } from './formatter.service';

interface IPageSettings {
    pageWidth: number;
    pageHeight: number;
    pageBorder: number;
    pageNumber: number;
}

interface ITableCell {
    width?: number;
    text: string;
}

interface ITextRenderSettings {
    x: number;
    y: number;
    fontName: string;
    fontSize: number;
    maxWidth: number;
    lineHeight: number;
    isBold?: boolean;
    isUnderline?: boolean;
    backgroundColor?: string;
}

interface IMeasureTextResult {
    width: number;
    height: number;
    lines: string[];
}

@Injectable()
export class PdfGeneratorService {
    private static readonly CoverBackgroundImageUrl = require('./img/cover-background.jpg');
    private static readonly ViegaLogoImageUrl = require('./img/viega-logo.png');
    private static readonly FontsToPrecache = ['viega-thin', 'viega'];
    private static readonly TextScaleFactor = 12;

    private coverBackgroundImage: HTMLImageElement;
    private viegaLogoImage: HTMLImageElement;
    /** id format is: {model-key}_{award-id} */
    private modelAwardImages: { [id: string]: HTMLImageElement } = {};
    private articleImages: { [id: string]: HTMLImageElement } = {};
    private hintImages: { [articleId: string]: HTMLImageElement } = {};
    private assetLoadingCompleted: boolean;
    /** Contains all urls which either have been loaded or are already loading. */
    private cacheImageElementMap: { [url: string]: HTMLImageElement } = {};
    private tempCanvas: HTMLCanvasElement = document.createElement('canvas');
    private hasFontsCached: boolean = false;

    constructor(
        private pathService: PathService,
        private lang: LangDataService,
        private formatter: FormatterService,
    ) {}

    /**
     * @param products The set of products to render. First item in the array is expected to be the plate, second should be the prewall.
     * @param matchingProducts A set of products for the plate of the opposite appliance.
     * @param specialColor A string indicating the special color (ral or metal) choosen by the user. It's important to locate the correct product images.
     */
    public generatePdf(
        products: IProduct[],
        matchingProducts: IProduct[],
        hints: { [articleNo: string]: IHint[] },
        specialColor?: SpecialColorSelection,
        renderOutput?: HTMLCanvasElement,
        includeMaterialsPage: boolean = true,
        metalColorsMap?: { [id: number]: IMetalColor },
        contactData?: IContactData
    ) {
        this.preloadImages(products.concat(matchingProducts), hints, specialColor, () => {
            const pdf: jsPDF = new jsPDF({
                orientation: 'portrait',
                unit: 'mm',
                format: 'a4'
            });

            const firstPlate: IModel = products[0].model;
            const modelHeaderText = firstPlate["header-text"];
            const modelHeaderTextPlain = modelHeaderText.replace("<br/>", " ");
            pdf.setProperties({
                title: modelHeaderTextPlain,
                subject: modelHeaderTextPlain,
                author: 'Viega',
                keywords: '',
                creator: 'Viega BPK2 - jsPDF' + pdf.version
            });

            // front page
            const pageSettings: IPageSettings = {
                pageWidth: 210,
                pageHeight: 297,
                pageBorder: 10,
                pageNumber: 2
            };
            this.renderProductSet(pdf, pageSettings, products, specialColor, renderOutput, includeMaterialsPage, metalColorsMap);

            if (matchingProducts.length > 0)
                this.renderProductSet(pdf, pageSettings, matchingProducts, specialColor, undefined, includeMaterialsPage, metalColorsMap);

            // hints page
            this.renderProductHintsPage(pdf, pageSettings, products.concat(matchingProducts), hints);

            // contact data page
            if (contactData !== undefined)
                this.renderContactDataPage(pdf, pageSettings, contactData);

            // legal page
            this.renderLegalInfoPage(pdf, pageSettings);

            const filename = modelHeaderTextPlain
                .replace(/[^a-z0-9äöüß]/gi, "_")
                .replace(/<br\/>/g, " ")
                .toLowerCase();
            pdf.save(`${filename}.pdf`);
        });
    }

    /**
     * Renders one set of products on a front page, a material page for the plate and a product page for each product.
     * @param products The set of products to render. First item in the array is expected to be the plate, second should be the prewall.
     */
    private renderProductSet(
        pdf: jsPDF,
        pageSettings: IPageSettings,
        products: IProduct[],
        specialColor?: SpecialColorSelection,
        renderOutput?: any,
        includeMaterialsPage: boolean = true,
        metalColorsMap?: { [id: number]: IMetalColor }
    ) {
        this.renderFrontPage(pdf, pageSettings, products[0], specialColor);

        // milieu / material page
        if (includeMaterialsPage) {
            const isMilieuConfigAvailable = renderOutput !== undefined;
            if (isMilieuConfigAvailable) {
                pdf.addPage();
                this.renderConfigurationPage(pdf, pageSettings, renderOutput);
            }

            pdf.addPage();
            this.renderMaterialPage(pdf, pageSettings, products);
        }

        // product pages
        products.forEach((product: IProduct, i: number) => {
            pdf.addPage();
            this.renderProductPage(pdf, pageSettings, product, i === 0 ? specialColor : undefined, metalColorsMap);
        });
    }

    private renderFrontPage(pdf: jsPDF, pageSettings: IPageSettings, product: IProduct, specialColor?: SpecialColorSelection) {
        const pageWidth = pageSettings.pageWidth;
        const pageHeight = pageSettings.pageHeight;
        const pageBorder = pageSettings.pageBorder;

        const frontPageWidth = pageWidth - pageBorder * 2;
        const frontPageHeight = pageHeight - pageBorder * 2;

        const canvas = this.drawFrontPageCover(frontPageWidth, frontPageHeight, product, specialColor);
        this.drawImage(pdf, canvas, pageBorder, pageBorder, frontPageWidth, frontPageHeight);

        // viega logo
        pdf.context2d.drawImage(<any>this.viegaLogoImage, pageWidth - pageBorder - 28.42, pageHeight - pageBorder - 25, 23.42, 20);

        // title
        const textSettings: ITextRenderSettings = {
            fontName: "viega-thin",
            fontSize: 8,
            x: pageBorder + 10,
            y: 40,
            maxWidth: 135,
            lineHeight: 10,
            backgroundColor: "#ffed00"
        };
        pdf.context2d.fillStyle = "#ffed00";
        pdf.context2d.fillRect(pageBorder - 0.1, 35, 150, 35);
        const headerLines = product.model["header-text"].split('<br/>');
        this.drawText(pdf, headerLines[0], textSettings);
        this.drawText(pdf, headerLines[1], textSettings);

        // awards
        let xOffset = pageBorder + 5;
        if (product.model.awards) {
            product.model.awards.forEach((awardId: string) => {
                const awardImage: HTMLImageElement | undefined = this.modelAwardImages[`${product.model.key}_${awardId}`];
                const isBrokenImage = awardImage.width === 0;
                if (!awardImage || isBrokenImage)
                    return;

                const imageWidth = awardImage.width / 8;
                const imageHeight = awardImage.height / 8;
                pdf.context2d.drawImage(<any>awardImage, xOffset, pageHeight - pageBorder - 5 - imageHeight, imageWidth, imageHeight);

                xOffset += imageWidth + 5;
            });
        }
    }

    private drawFrontPageCover(width: number, height: number, product: IProduct, specialColor?: SpecialColorSelection) {
        const scaleFactor = 12;
        const canvasWidth = width * scaleFactor;
        const canvasHeight = height * scaleFactor;

        const canvas = document.createElement('canvas') as HTMLCanvasElement;
        canvas.width = canvasWidth;
        canvas.height = canvasHeight;
        const context = canvas.getContext('2d') as CanvasRenderingContext2D;

        // draw blurry background image
        context.drawImage(this.coverBackgroundImage, 0, 0, canvasWidth, canvasHeight);

        // article image
        let specialColorIdentifier;
        if (specialColor)
            specialColorIdentifier = specialColor.stringRepresentation;

        const plateImage: HTMLImageElement = this.articleImages[`${product.article.key}_${specialColorIdentifier}_fv`];
        const isUrinal = product.model.attributes["plate-appliance"][0] === 0;
        context.fillStyle = "#000";
        context.font = `${2 * scaleFactor}px viega`;
        context.textAlign = "right";
        context.textBaseline = "top";
        if (plateImage !== undefined && plateImage.width > 0) {
            const imageAspect = plateImage.height / plateImage.width;

            let articleImageX: number;
            const articleImageY = 90 * scaleFactor;
            let articleImageWidth: number;
            if (isUrinal) {
                articleImageX = 42.5 * scaleFactor;
                articleImageWidth = 85 * scaleFactor;
            } else {
                articleImageX = 10 * scaleFactor;
                articleImageWidth = 170 * scaleFactor;
            }

            const articleImageHeight = articleImageWidth * imageAspect;
            context.drawImage(plateImage, articleImageX, articleImageY, articleImageWidth, articleImageHeight);

            const disclaimerX = articleImageX + articleImageWidth;
            const disclaimerY = articleImageY + (96 * scaleFactor);
            // context.fillText(this.lang.getString('result-page-image-disclaimer'), disclaimerX, disclaimerY, articleImageWidth);
        }

        return canvas;
    }

    private renderConfigurationPage(pdf: jsPDF, pageSettings: IPageSettings, renderOutput: HTMLCanvasElement) {
        const pageWidth = pageSettings.pageWidth;
        const pageHeight = pageSettings.pageHeight;
        const pageBorder = pageSettings.pageBorder;
        const pageTitle = this.lang.getString("result-page-configuration");

        this.renderGenericPage(pdf, pageSettings, pageTitle, pageSettings.pageNumber++);

        // draw milieu screenshot
        let currentY = pageBorder + 30;
        this.drawImage(pdf, renderOutput, pageBorder, currentY, 190, 190);
        currentY += 190;
    }

    private renderMaterialPage(pdf: jsPDF, pageSettings: IPageSettings, products: IProduct[]) {
        const pageBorder = pageSettings.pageBorder;

        const pageTitle = this.lang.getString("result-page-materiallist");
        this.renderGenericPage(pdf, pageSettings, pageTitle, pageSettings.pageNumber++);

        const currentY = pageBorder + 30;
        const tableHeader = [{
            text: this.lang.getString("result-page-materiallist-position"),
            width: 20,
        }, {
            text: this.lang.getString("result-page-materiallist-description"),
            width: 160 - pageBorder * 2,
        }, {
            text: this.lang.getString("result-page-materiallist-article"),
            width: 30,
        }];
        const tableRows: ITableCell[][] = products.map((product: IProduct, i: number) => {
            return [
                { text: (i + 1).toString() },
                { text: product.model["header-text"].replace(/<br\/>/g, " ") },
                { text: this.formatter.formatArticleNumber(product.article["article-number"]) },
            ];
        });
        tableRows.splice(0, 0, tableHeader);

        pdf.setLineWidth(0.05);
        this.drawTable(pdf, pageBorder, currentY, tableRows);
    }

    private renderProductPage(
        pdf: jsPDF,
        pageSettings: IPageSettings,
        product: IProduct,
        specialColor?: SpecialColorSelection,
        metalColorsMap?: { [id: number]: IMetalColor }
    ) {
        const pageWidth = pageSettings.pageWidth;
        const pageHeight = pageSettings.pageHeight;
        const pageBorder = pageSettings.pageBorder;
        const imageBorder = 10;
        let imageWidth = 45;
        const imagePadding = 5;

        this.renderGenericPage(pdf, pageSettings, this.lang.getString("result-page-materiallist-productinformation"), pageSettings.pageNumber++);

        // prepare images
        let specialColorIdentifier;
        if (specialColor)
            specialColorIdentifier = specialColor.stringRepresentation;

        const productImages: HTMLImageElement[] = [];
        const fvImage: HTMLImageElement = this.articleImages[`${product.article.key}_${specialColorIdentifier}_fv`];
        const modelGroup = product.model.attributes["model-group"];
        const isPlate = modelGroup !== undefined && modelGroup[0] === 2;
        if (fvImage !== undefined && fvImage.width > 0)
            productImages.push(fvImage);
        const ppImage: HTMLImageElement = this.articleImages[`${product.article.key}_${specialColorIdentifier}_pp`];
        if (isPlate && ppImage !== undefined && ppImage.width > 0)
            productImages.push(ppImage);
        const sdImage: HTMLImageElement = this.articleImages[`${product.article.key}_sd`];
        if (sdImage !== undefined && sdImage.width > 0)
            productImages.push(sdImage);

        // render borders + images
        pdf.context2d.lineWidth = 1;
        pdf.context2d.strokeStyle = "#ccc";

        let currentX = pageBorder;
        let currentY = pageBorder + 30;
        let imageBorderWidth = imageWidth + (imageBorder * 2);

        productImages.forEach((image: HTMLImageElement) => {
            const imageAspect = image.height / image.width;
            if (imageAspect > 3.0) {
                imageWidth -= 5;
                imageBorderWidth -= 5;
            }
            const imageHeight = imageWidth * imageAspect;
            const imageBorderHeight = imageHeight + (imageBorder * 2);

            pdf.context2d.strokeRect(currentX, currentY, imageBorderWidth, imageBorderHeight);
            this.drawImage(pdf, image, currentX + imageBorder, currentY + imageBorder, imageWidth, imageHeight);

            currentY += imageBorderHeight + imagePadding;
        });
        currentX += imageBorderWidth + 5;

        // description text
        const textSettings: ITextRenderSettings = {
            fontName: 'viega',
            fontSize: 3,
            x: currentX,
            y: pageBorder + 30,
            maxWidth: pageWidth - pageBorder - currentX,
            lineHeight: 4,
        };

        // new: usage of the article html text instead of the model html text
        this.drawSimpleHtml(pdf, product.article["catalog-text"], textSettings);

        // model no
        /*textSettings.y += 0.5;
        const modelNoString = `${this.lang.getString("model-label")} ${product.model["model-number"]}`;
        textSettings.isBold = true;
        this.drawText(pdf, modelNoString, textSettings);
        textSettings.isBold = false;
        */

        // article extra info
        const isPrewall = product.model.attributes["model-group"][0] === 3;
        if (isPrewall) {
            /*const buildHeightString = this.lang.getString("build-height-label");
            if (product.article['build-height'] !== undefined) {
                const merkmalString = `${buildHeightString} ${product.article['build-height'].values[0]}`;
                this.drawText(pdf, merkmalString, textSettings);
            }
            if (product.article['build-height-range'] !== undefined) {
                const merkmalString = `${buildHeightString} ${product.article['build-height-range'].values[0]}`;
                this.drawText(pdf, merkmalString, textSettings);
            }*/
        } else {
            /*if (product.article['cover-plate-material'] !== undefined) {
                textSettings.y += 5; // empty line

                const name = product.article['cover-plate-material'].name;
                const value = product.article['cover-plate-material'].values[0];
                textSettings.isBold = true;
                this.drawText(pdf, name, textSettings);
                textSettings.isBold = false;
                this.drawText(pdf, value, textSettings);
            }
            if (product.article['pressure-piece-material'] !== undefined) {
                textSettings.y += 0.5;

                const name = product.article['pressure-piece-material'].name;
                const value = product.article['pressure-piece-material'].values[0];
                textSettings.isBold = true;
                this.drawText(pdf, name, textSettings);
                textSettings.isBold = false;
                this.drawText(pdf, value, textSettings);
            }*/
        }

        if (specialColor !== undefined) {
            /*let headingString;
            let colorString;
            if (specialColor.isRalColor) {
                headingString = this.lang.getString('special-color-plate');

                const ralColorId = specialColor.colorId;
                if (specialColor.isRalMatt)
                    colorString = `${this.lang.getString('special-color-ral-matt')} ${ralColorId}`;
                else
                    colorString = `${this.lang.getString('special-color-ral-glossy')} ${ralColorId}`;
            } else {
                const metalColorId = specialColor.colorId;
                headingString = this.lang.getString('special-color-metal');
                colorString = this.lang.getString(metalColorsMap[metalColorId]["label-ref"]);
            }

            textSettings.y += 0.5;
            textSettings.isBold = true;
            this.drawText(pdf, headingString, textSettings);
            textSettings.isBold = false;
            this.drawText(pdf, colorString, textSettings);*/
        }

        /*textSettings.y += 0.5;
        const articleNoString = `${this.lang.getString("article-label")} ${this.formatter.formatArticleNumber(product.article["article-number"])}`;
        textSettings.isBold = true;
        this.drawText(pdf, articleNoString, textSettings);
        textSettings.isBold = false;*/
    }

    private drawImage(pdf: jsPDF, imageElement: HTMLImageElement | HTMLCanvasElement, x: number, y: number, width: number, height: number) {
        const imageWidth = imageElement.width;
        const imageHeight = imageElement.height;
        this.tempCanvas.width = imageWidth;
        this.tempCanvas.height = imageHeight;

        const context = this.tempCanvas.getContext("2d");
        context.fillStyle = "#fff";
        context.fillRect(0, 0, imageWidth, imageHeight);
        context.drawImage(imageElement, 0, 0);

        pdf.addImage(<any>this.tempCanvas.toDataURL('image/jpeg'), 'JPEG', x, y, width, height, undefined, <any>'FAST');
    }

    private renderProductHintsPage(pdf: jsPDF, pageSettings: IPageSettings, products: IProduct[], hints: { [articleNo: string]: IHint[] }) {
        const pageWidth = pageSettings.pageWidth;
        const pageHeight = pageSettings.pageHeight;
        const pageBorder = pageSettings.pageBorder;
        const imageWidth = pageWidth - (pageBorder * 2) - 10;
        const imageBorderWidth = imageWidth + 10;
        const isPageAdded = false;

        let currentY = pageBorder + 30;
        Object.keys(hints).forEach((articleNo: string) => {
            const product = products.find(p => p.article["article-number"] === articleNo);
            const articleHints: IHint[] = hints[articleNo];
            if (product !== undefined && articleHints !== undefined) {
                if (!isPageAdded) {
                    pdf.addPage();
                    this.renderGenericPage(pdf, pageSettings, this.lang.getString("result-page-installation-hints-heading"), pageSettings.pageNumber++);
                }

                const modelText = this.lang.getString("model-label");
                const articleText = this.lang.getString("article-label");
                const modelName = product.model["header-text"].replace(/<br\/>/g, " ");
                const modelNo = product.model["model-number"];

                // heading
                const textSettings: ITextRenderSettings = {
                    fontName: 'viega',
                    fontSize: 4,
                    x: pageBorder,
                    y: currentY,
                    maxWidth: pageWidth - (pageBorder * 2),
                    lineHeight: 5,
                    isBold: true
                };
                this.drawText(pdf, modelName, textSettings);

                // sub headings
                textSettings.fontSize = 3;
                textSettings.isBold = false;
                textSettings.lineHeight = 4;
                this.drawText(pdf, `${modelText} ${modelNo}, ${articleText} ${articleNo}`, textSettings);

                currentY = textSettings.y + 5;
                articleHints.forEach((hint: IHint) => {
                    // image
                    const image = this.hintImages[hint["image-id"]];
                    const imageAspect = image.height / image.width;
                    const imageHeight = imageWidth * imageAspect;
                    const imageBorderHeight = imageHeight + 10;
                    textSettings.y = currentY;
                    pdf.context2d.lineWidth = 1;
                    pdf.context2d.strokeStyle = "#ccc";
                    pdf.context2d.strokeRect(pageBorder, currentY, imageBorderWidth, imageBorderHeight);
                    pdf.context2d.drawImage(<any>image, pageBorder + 5, currentY + 5, imageWidth, imageHeight);
                    currentY += imageHeight + 15;

                    // description
                    const hintText = this.lang.getString(hint["label-ref"]);
                    textSettings.fontName = 'viega';
                    textSettings.x = pageBorder;
                    textSettings.y = currentY;
                    textSettings.maxWidth = pageWidth - (pageBorder * 2) - 15;
                    textSettings.fontSize = 3;
                    textSettings.lineHeight = 4;
                    this.drawSimpleHtml(pdf, hintText, textSettings);

                    currentY = Math.max(textSettings.y, currentY + imageBorderHeight) + 5;
                });
            }
        });
    }

    private renderContactDataPage(pdf: jsPDF, pageSettings: IPageSettings, contactData: IContactData) {
        const pageWidth = pageSettings.pageWidth;
        const pageHeight = pageSettings.pageHeight;
        const pageBorder = pageSettings.pageBorder;

        pdf.addPage();
        this.renderGenericPage(pdf, pageSettings, this.lang.getString('result-page-contact-page-heading'), pageSettings.pageNumber++, false);

        const textSettings: ITextRenderSettings = {
            fontName: 'viega',
            fontSize: 4,
            x: pageBorder,
            y: pageBorder + 30,
            maxWidth: pageWidth - (pageBorder * 2),
            lineHeight: 5,
        };
        if (contactData.project) {
            textSettings.isBold = true;
            this.drawText(pdf, `${this.lang.getString('result-page-contact-project')} ${contactData.project}`, textSettings);
            textSettings.isBold = false;
            textSettings.y += 3;
        }
        if (contactData.company)
            this.drawText(pdf, `${this.lang.getString('result-page-contact-company')} ${contactData.company}`, textSettings);
        if (contactData.contactPerson)
            this.drawText(pdf, contactData.contactPerson, textSettings);
        if (contactData.street)
            this.drawText(pdf, contactData.street, textSettings);
        if (contactData.zipCode && contactData.city)
            this.drawText(pdf, `${contactData.zipCode} ${contactData.city}`, textSettings);
        if (contactData.phone) {
            textSettings.y += 3;
            this.drawText(pdf, `${this.lang.getString('result-page-contact-phone')} ${contactData.phone}`, textSettings);
        }
        if (contactData.email)
            this.drawText(pdf, contactData.email, textSettings);
        if (contactData.website)
            this.drawText(pdf, contactData.website, textSettings);
    }

    private renderLegalInfoPage(pdf: jsPDF, pageSettings: IPageSettings) {
        const impressumHtmlString = this.lang.getString("pdf-impressum-text");
        
        if (!impressumHtmlString || impressumHtmlString.length < 1)
            return;

        const pageWidth = pageSettings.pageWidth;
        const pageHeight = pageSettings.pageHeight;
        const pageBorder = pageSettings.pageBorder;

        pdf.addPage();
        this.renderGenericPage(pdf, pageSettings, null, pageSettings.pageNumber++, false);

        const textSettings: ITextRenderSettings = {
            fontName: 'viega',
            fontSize: 4,
            x: pageBorder,
            y: pageBorder + 140,
            maxWidth: pageWidth - (pageBorder * 2),
            lineHeight: 5,
        };
        const impressumHeadingString = this.lang.getString("pdf-impressum");
        this.drawText(pdf, impressumHeadingString, textSettings);

        textSettings.y += 5;
        textSettings.fontSize = 3;
        textSettings.lineHeight = 4;
        this.drawSimpleHtml(pdf, impressumHtmlString, textSettings);
    }

    private drawSimpleHtml(pdf: jsPDF, html: string, textSettings: ITextRenderSettings) {
        const domElement = document.createElement('div');
        let cleanHtml = html
            // .replace(/<div[^>]*>\s*<span[^>]*>-<\/span>([^<]+<\/div>)<\/div>/g, '<li>$1</li>')
            .replace(/<div[^>]*>\s*<\/div>/g, '');

        // since we use article html from PSP, br elements between model number and article attributes need to be taken care of
        // otherwise we wouldn't be able to show a gap between model number and article attributes
        cleanHtml = cleanHtml.replace(/<\/b><\/div>((<br\/><div( ){1}name)|(<br\/><div( ){2}name))='articledata'/ig, "<\/b><\/div><br\/><br\/><div name='articledata'");

        domElement.innerHTML = cleanHtml;

        this.drawDomNode(pdf, domElement, textSettings);
    }

    private drawDomNode(pdf: jsPDF, node: Node, textSettings: ITextRenderSettings) {
        const listItemPaddingLeft = 0;
        const listItemPaddingRight = 3;
        const listItemPadding = listItemPaddingLeft + listItemPaddingRight;
        let boldText = '';

        for (let i = 0; i < node.childNodes.length; i++) {
            const childNode = node.childNodes.item(i);

            if (childNode.nodeType === Node.TEXT_NODE) {
                if (childNode.nodeValue.trim().length > 0)
                    this.drawText(pdf, childNode.nodeValue, textSettings);
            } else if (childNode.nodeName === 'B') {
                boldText += childNode.textContent;
            } else if (childNode.nodeName === 'U') {
                textSettings.isUnderline = true;
                this.drawDomNode(pdf, childNode, textSettings);
                textSettings.isUnderline = false;
            } else if (childNode.nodeName === 'SPAN' && childNode.textContent === '-') {
                textSettings.x += listItemPaddingLeft;
                textSettings.maxWidth -= listItemPaddingLeft;

                this.drawText(pdf, '-', textSettings);
                // don't move one line down this time
                textSettings.y -= textSettings.lineHeight;

                textSettings.x += listItemPaddingRight;
                textSettings.maxWidth -= listItemPaddingRight;
                const text = childNode.nextSibling.textContent;
                this.drawText(pdf, text, textSettings);

                textSettings.x -= listItemPadding;
                textSettings.maxWidth += listItemPadding;

                // skip next sibling
                i++;
            } else if (childNode.nodeName === 'BR' && i > 0) {
                const previousNode = node.childNodes.item(i - 1);
                if (previousNode.nodeName === 'BR')
                    textSettings.y += textSettings.lineHeight;
            } else {
                this.drawDomNode(pdf, childNode, textSettings);
            }
        }

        if (boldText.length > 0) {
            textSettings.isBold = true;
            this.drawText(pdf, boldText, textSettings);
            textSettings.isBold = false;
        }
    }

    private drawTable(pdf: jsPDF, x: number, y: number, cells: ITableCell[][], fontSize: number = 3.5, minRowHeight: number = 8) {
        const cellPaddingX = 2;
        const cellPaddingY = 2;
        let currentY = y;

        // rows
        cells.forEach((row: ITableCell[], rowIndex: number) => {
            const isHeader = rowIndex === 0;
            let currentX = x;

            const textSettings: ITextRenderSettings = {
                x: 0,
                y: 0,
                maxWidth: 0,
                fontName: "viega",
                fontSize,
                lineHeight: 4,
                backgroundColor: isHeader ? "#ccc" : undefined
            };

            const rowHeight = this.measureTableRowHeight(cells[0], row, textSettings, cellPaddingX, cellPaddingY, minRowHeight);

            row.forEach((cell: ITableCell, colIndex: number) => {
                // take given cell width, or the width of the corresponding header cell
                const cellWidth = cell.width || cells[0][colIndex].width;
                textSettings.maxWidth = cellWidth - 3;

                this.drawTableCell(pdf, cell.text, currentX, currentY, cellWidth, rowHeight, cellPaddingX, cellPaddingY, isHeader, textSettings);
                currentX += cellWidth;
            });

            currentY += rowHeight;
        });
    }

    private measureTableRowHeight(headerRowCells: ITableCell[], rowCells: ITableCell[], textSettings: ITextRenderSettings, paddingX: number, paddingY: number, minRowHeight: number): number {
        const cellTextSettings = Object.assign(textSettings);

        let rowHeight = minRowHeight - (paddingY * 2);
        rowCells.forEach((cell: ITableCell, colIndex: number) => {
            // take given cell width, or the width of the corresponding header cell
            const cellWidth = cell.width || headerRowCells[colIndex].width;
            cellTextSettings.maxWidth = cellWidth - (paddingX * 2);

            const measureResult: IMeasureTextResult = this.measureText(cell.text, cellTextSettings);
            rowHeight = Math.max(rowHeight, measureResult.height);
        });

        return rowHeight + (paddingY * 2);
    }

    private drawTableCell(pdf: jsPDF, text: string, x: number, y: number, width: number, height: number, paddingX: number, paddingY: number, isHeader: boolean, textSettings: ITextRenderSettings) {
        const cellTextSettings = Object.assign(textSettings);
        cellTextSettings.x = x + paddingX;
        cellTextSettings.y = y + paddingY;
        cellTextSettings.maxWidth = width - (paddingX * 2);

        if (isHeader) {
            pdf.context2d.fillStyle = "#ccc";
            pdf.context2d.fillRect(x, y, width, height);
        }

        pdf.context2d.strokeRect(x, y, width, height);
        this.drawText(pdf, text, textSettings);
    }

    // private getTableCell

    private renderGenericPage(pdf: jsPDF, pageSettings: IPageSettings, pageTitle: string, pageNumber: number, includeImageDisclaimer: boolean = true) {
        const pageWidth = pageSettings.pageWidth;
        const pageHeight = pageSettings.pageHeight;
        const pageBorder = pageSettings.pageBorder;

        if (pageTitle) {
            // heading
            this.drawText(pdf, pageTitle, {
                fontName: "viega-thin",
                fontSize: 7,
                x: pageBorder,
                y: pageBorder + 5,
                maxWidth: 140,
                lineHeight: 9
            });
        }

        // viega logo
        pdf.context2d.drawImage(<any>this.viegaLogoImage, pageWidth - pageBorder - 29.275, pageBorder, 29.275, 25);

        // image disclaimer
        const textSettings: ITextRenderSettings = {
            fontName: 'viega',
            fontSize: 2.5,
            x: pageBorder,
            y: pageHeight - pageBorder,
            maxWidth: pageWidth - (pageBorder * 2),
            lineHeight: 3.5,
        };
        if (includeImageDisclaimer) {
            const imageDisclaimerString = this.lang.getString('result-page-image-disclaimer');
            this.drawText(pdf, imageDisclaimerString, textSettings);
        }

        // page number
        textSettings.x = pageWidth - pageBorder - 4;
        textSettings.y = pageHeight - pageBorder;
        textSettings.maxWidth = 4;
        this.drawText(pdf, pageNumber.toString(), textSettings);
    }

    /**
     * @returns The total height of the drawn text.
     */
    private drawText(pdf: jsPDF, text: string, textSettings: ITextRenderSettings) {
        // this is important, because an invalid data url would be generated when the canvas is converted
        if (text === '')
            return;

        const scaleFactor = PdfGeneratorService.TextScaleFactor;
        let font = textSettings.fontName;
        const fontSize = textSettings.fontSize;
        const maxWidth = textSettings.maxWidth;
        const lineHeight = textSettings.lineHeight;
        const lineHeightScaled = lineHeight * scaleFactor;
        const isUnderline = textSettings.isUnderline === true;

        if (textSettings.isBold)
            font = "viega-bold";

        const measureResult: IMeasureTextResult = this.measureText(text, textSettings);

        this.tempCanvas.width = maxWidth * scaleFactor;
        this.tempCanvas.height = measureResult.lines.length * lineHeightScaled;

        const context = this.tempCanvas.getContext('2d');
        context.textBaseline = "top";
        context.font = `${fontSize * scaleFactor}px ${font}`;

        // use this and PNG for transparent text
        // context.clearRect(0, 0, this.tempCanvas.width, this.tempCanvas.height);
        context.fillStyle = textSettings.backgroundColor || "#fff";
        context.fillRect(0, 0, this.tempCanvas.width, this.tempCanvas.height);

        // the context looses its settings when the canvas was resized
        context.fillStyle = "#000";
        context.textBaseline = "top";
        context.font = `${fontSize * scaleFactor}px ${font}`;
        context.strokeStyle = "#000"; // for underline text
        context.lineWidth = 0.33 * scaleFactor;
        measureResult.lines.forEach((line: string, i: number) => {
            if (line.length > 0) {
                const destY = i * lineHeightScaled;
                context.fillText(line, 0, destY);

                if (isUnderline) {
                    const textWidth = context.measureText(line).width;
                    const lineY = destY + lineHeightScaled - scaleFactor;
                    context.moveTo(0, lineY);
                    context.lineTo(textWidth, lineY);
                    context.stroke();
                }
            }
        });

        pdf.addImage(this.tempCanvas.toDataURL('image/jpeg'), "JPEG", textSettings.x, textSettings.y, maxWidth, measureResult.height, undefined, <any>'FAST');
        textSettings.y += measureResult.height;
    }

    private measureText(text: string, textSettings: ITextRenderSettings): IMeasureTextResult {
        const context = this.tempCanvas.getContext('2d');

        const scaleFactor = PdfGeneratorService.TextScaleFactor;
        const fontSize = textSettings.fontSize;
        const maxWidth = textSettings.maxWidth;
        const maxWidthScaled = maxWidth * scaleFactor;
        const lineHeight = textSettings.lineHeight;

        let font = textSettings.fontName;
        if (textSettings.isBold)
            font = "viega-bold";

        context.textBaseline = "top";
        context.font = `${fontSize * scaleFactor}px ${font}`;

        // aggregate the lines by word wrapping existing words
        const textWords = text.split(/\s/g);
        let lineWidth = 0;
        let currentLine = '';
        const lines: string[] = [];

        let totalTextHeight = 0;
        textWords.forEach((word: string, i: number) => {
            const wordWidth = context.measureText(word + ' ').width;
            lineWidth += wordWidth;

            const isEndOfLine = lineWidth > maxWidthScaled;
            const isEndOfText = i === textWords.length - 1;
            if (isEndOfLine || isEndOfText) {
                if (isEndOfText && !isEndOfLine)
                    currentLine += word;

                lines.push(currentLine);
                lineWidth = 0;
                currentLine = '';
                totalTextHeight += lineHeight;

                if (isEndOfLine && isEndOfText) {
                    lines.push(word);
                    totalTextHeight += lineHeight;
                } else {
                    currentLine += word + ' ';
                    lineWidth += wordWidth;
                }
            } else {
                currentLine += word + ' ';
            }
        });

        return {
            lines,
            width: maxWidth,
            height: totalTextHeight
        } as IMeasureTextResult;
    }

    /**
     * There may be fonts used by the canvas renderer which are not used on the page where the generator service is invoked and thus the fonts to render the
     * pdf may not be available yet. This function will start preloading the respective fonts to solve this issue.
     */
    public preloadFonts() {
        if (this.hasFontsCached)
            return;

        PdfGeneratorService.FontsToPrecache.forEach((fontName: string) => {
            const tempSpan = document.createElement('span');
            tempSpan.style.visibility = 'hidden';
            tempSpan.style.fontFamily = fontName;
            tempSpan.innerHTML = '.';
            document.documentElement.appendChild(tempSpan);
        });

        this.hasFontsCached = true;
    }

    /**
     * @param specialColor A string indicating the special color (ral or metal) choosen by the user. It's important to locate the correct product images.
     */
    public preloadImages(forProducts: IProduct[], forHints: { [articleNo: string]: IHint[] }, specialColor: SpecialColorSelection, completed?: () => void) {
        let assetLoad = 0;
        const assetLoadCounter = () => {
            assetLoad--;
            if (assetLoad === 0 && completed !== undefined)
                completed();
        };

        assetLoad = 2;
        this.coverBackgroundImage = this.preloadImage(PdfGeneratorService.CoverBackgroundImageUrl, assetLoadCounter);
        this.viegaLogoImage = this.preloadImage(PdfGeneratorService.ViegaLogoImageUrl, assetLoadCounter);

        forProducts.forEach((product: IProduct) => {
            const model = product.model;
            if (model.awards && model.awards.length > 0) {
                assetLoad += model.awards.length;

                model.awards.forEach((id: string) => {
                    const awardImageCacheKey = `${model.key}_${id}`;
                    const awardUrl = this.pathService.modelAwardImagePath(model, id);
                    this.modelAwardImages[awardImageCacheKey] = this.preloadImage(awardUrl, assetLoadCounter);
                });
            }

            const article = product.article;
            const isSpecialColorArticle = article.attributes["special-type"] !== null && article.attributes["special-type"] !== undefined;
            const isGoldArticle = isSpecialColorArticle && article.attributes["special-type"][0] === 2;
            let specialColorIdentifier;
            if (isSpecialColorArticle && !isGoldArticle)
                specialColorIdentifier = specialColor.stringRepresentation;

            if (article["fv-image-url"] !== undefined) {
                assetLoad++;
                const imageUrl = this.pathService.articleImagePath(product.model, product.article, 'front', 'large', specialColorIdentifier);
                this.articleImages[`${article.key}_${specialColorIdentifier}_fv`] = this.preloadImage(imageUrl, assetLoadCounter);
            }
            if (article["pp-image-url"] !== undefined) {
                assetLoad++;
                const imageUrl = this.pathService.articleImagePath(product.model, product.article, 'perspective', 'large', specialColorIdentifier);
                this.articleImages[`${article.key}_${specialColorIdentifier}_pp`] = this.preloadImage(imageUrl, assetLoadCounter);
            }
            if (model["sd-image-url"] !== undefined) {
                assetLoad++;
                const imageUrl = this.pathService.modelImagePath(product.model, 'scaledrawing', 'large');
                this.articleImages[`${article.key}_sd`] = this.preloadImage(imageUrl, assetLoadCounter);
            }

            const hints = forHints[product.article["article-number"]];
            if (hints) {
                hints.forEach((hint: IHint) => {
                    if (this.hintImages[hint["image-id"]] === undefined) {
                        assetLoad++;

                        const hintUrl = this.pathService.hintImagePath(hint);
                        this.hintImages[hint["image-id"]] = this.preloadImage(hintUrl, assetLoadCounter);
                    }
                });
            }
        });
    }

    private preloadImage(src: string, completed: () => void): HTMLImageElement {
        const cachedElement = this.cacheImageElementMap[src];
        const isAlreadyCached = cachedElement !== undefined;
        if (isAlreadyCached) {
            if (cachedElement.complete)
                completed();
            else
                cachedElement.addEventListener('load', completed);

            return cachedElement;
        }

        const imageElement = document.createElement('img');
        imageElement.src = src;
        imageElement.style.backgroundColor = "#fff";
        imageElement.addEventListener('load', completed);
        imageElement.addEventListener('error', completed);

        return imageElement;
    }
}



// WEBPACK FOOTER //
// ./src/app/core/pdf-generator.service.ts